import { FieldValues, UseFormGetValues } from 'react-hook-form'
import { normalizeStartDate } from './calculateExpirationDate.utils'
import {
  resolveAncestorCacheRef,
  resolveAncestorFieldRef,
} from './formAncestry.utils'
import { getRuleCache } from './formBehaviorRuleCache.registry'
import { resolveFieldRef } from './formBehavior.utils'
import {
  FormRuleParamRef,
  FormRuleParamRefAncestorByEntity,
  FormRuleParamRefAncestorCacheByEntity,
  FormRuleParamRefAncestorCacheParent,
  FormRuleParamRefAncestorParent,
  FormRuleParamRefCache,
  FormRuleParamRefField,
} from '@app-store'
import { FormAncestryRuntimeEntry } from '../types/formBehavior.types'

const resolveFieldParam = (
  param: FormRuleParamRefField,
  getValues: UseFormGetValues<FieldValues>
): unknown => {
  const keyName = param.key_name?.trim()
  if (!keyName) return undefined
  if (
    param.path === undefined &&
    (keyName === 'start_date' || keyName.endsWith('_date'))
  ) {
    return normalizeStartDate(getValues(keyName)) ?? undefined
  } else if (param.path === undefined) {
    return getValues(keyName)
  }
  return resolveFieldRef(getValues, keyName, param.path ?? 'id')
}

const resolveCacheParam = (
  layoutUid: string,
  param: FormRuleParamRefCache,
  getValues: UseFormGetValues<FieldValues>
): unknown => {
  const rows = getRuleCache(layoutUid, param.cache_key)
  if (!Array.isArray(rows) || !rows.length) return undefined
  const matchValue = resolveFieldParam(param.match, getValues)
  if (matchValue == null || matchValue === '') return undefined
  const row = rows.find(
    (entry) => String(entry[param.row_field]) === String(matchValue)
  )
  if (!row) return undefined
  return row[param.property]
}

const resolveAncestorParam = (
  param: FormRuleParamRefAncestorByEntity | FormRuleParamRefAncestorParent,
  ancestors: FormAncestryRuntimeEntry[]
): unknown => resolveAncestorFieldRef(ancestors, param)

const resolveAncestorCacheParam = (
  param:
    | FormRuleParamRefAncestorCacheByEntity
    | FormRuleParamRefAncestorCacheParent,
  ancestors: FormAncestryRuntimeEntry[]
): unknown => resolveAncestorCacheRef(ancestors, param)

export const resolveCacheProperty = (
  layoutUid: string,
  cacheKey: string,
  matchKeyName: string,
  rowField: string,
  property: string,
  getValues: UseFormGetValues<FieldValues>
): unknown =>
  resolveCacheParam(
    layoutUid,
    {
      from: 'cache',
      cache_key: cacheKey,
      row_field: rowField,
      property,
      match: { from: 'field', key_name: matchKeyName, path: 'id' },
    },
    getValues
  )

export const resolveFormRuleParam = (
  layoutUid: string,
  param: FormRuleParamRef,
  getValues: UseFormGetValues<FieldValues>,
  ancestors: FormAncestryRuntimeEntry[] = []
): unknown => {
  if (param.from === 'field') {
    return resolveFieldParam(param, getValues)
  }
  if (param.from === 'ancestor_field') {
    return resolveAncestorParam(param, ancestors)
  }
  if (param.from === 'ancestor_cache') {
    return resolveAncestorCacheParam(param, ancestors)
  }
  return resolveCacheParam(layoutUid, param, getValues)
}

export const resolveFormRuleParams = (
  layoutUid: string,
  params: FormRuleParamRef[] | undefined,
  getValues: UseFormGetValues<FieldValues>,
  ancestors: FormAncestryRuntimeEntry[] = []
): unknown[] | null => {
  if (!params?.length) return null
  const resolved: unknown[] = []
  for (const param of params) {
    const value = resolveFormRuleParam(layoutUid, param, getValues, ancestors)
    if (value === undefined || value === null || value === '') {
      return null
    }
    resolved.push(value)
  }
  return resolved
}
